<template lang="pug">
div
  .treatment-plan
    .treatment-filters
      v-row.px-4.py-2(no-gutters)
        v-col.grow
          div.d-flex.align-center
            search-treatment-dropdown(@added="handleAddItem($event)" size="xxs" input-text-size="text-xs")
            y-button.ml-2(label="Add in bulk" color="neutral300" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="openAddBulkTreatmentDialog")
            y-button.ml-2(label="Add from past visit" color="neutral300" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="openAddPastTreatmentDialog")
        v-col.text-end(cols="2")
          y-button(label="Edit plan" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="openTreatmentPlanDialog")
      v-divider
      v-progress-linear(v-if="isFetchingTreatments || isFetchingActiveMedication || isInjecting" :width="3" rounded color="primary" indeterminate)
      v-tabs.mt-2(v-model="tabs" height="36" grow @change="changeTab")
        v-tab.px-0
          div.text-sm.normal-case.mb-2 All
            span.text-xs.font-medium.custom-chip.neutral100--text(:style="`background-color: ${!tabs ? 'var(--v-neutral500)' : 'var(--v-neutral400)'}`") {{ treatments.length }}
        v-tab.px-0
          div.text-sm.normal-case.mb-2 Items
            span.text-xs.font-medium.custom-chip.neutral100--text(:style="`background-color: ${tabs === 1 ? 'var(--v-neutral500)' : 'var(--v-neutral400)'}`") {{ itemList.length }}
        v-tab.px-0
          div.text-sm.normal-case.mb-2 Services
            span.text-xs.font-medium.custom-chip.neutral100--text(:style="`background-color: ${tabs === 2 ? 'var(--v-neutral500)' : 'var(--v-neutral400)'}`") {{ serviceList.length }}
        v-tab.px-0
          div.text-sm.normal-case.mb-2 Packages
            span.text-xs.font-medium.custom-chip.neutral100--text(:style="`background-color: ${tabs === 3 ? 'var(--v-neutral500)' : 'var(--v-neutral400)'}`") {{ packageList.length }}
        v-tab.px-0
          div.text-sm.normal-case.mb-2 Documents
            span.text-xs.font-medium.custom-chip.neutral100--text(:style="`background-color: ${tabs === 4 ? 'var(--v-neutral500)' : 'var(--v-neutral400)'}`") {{ documentList.length }}
        v-tabs-slider(style="width: 40px; place-self: center")
      v-divider

    template(v-for="(form, idx) in treatmentAddForms")
      treatment-list-form(:treatment="form" @remove="removeForm(idx)" @updated="updateForm(idx)" :key="`form-${form.listid}-${form.id}`")
    template(v-if="isFetchingTreatments || isFetchingActiveMedication || isInjecting")
      v-skeleton-loader.px-4(type="list-item-three-line@3")
    template(v-else-if="treatmentList.length === 0 && treatmentAddForms.length === 0")
      div.d-flex.align-center.justify-center.fill-height(style="min-height: 200px;")
        div(align="center")
          v-icon(size="32" color="neutral500") $yzi-file-02
          div.text-sm.font-medium.neutral700--text.mt-2 No treatment and services selected yet
          div.text-sm.neutral500--text.mt-1 Start adding treatment to see treatment plan here
          y-button.mt-2(label="Add treatment" color="neutral300" text-color="neutral700" prepend-icon="mdi-plus" icon-size="16" icon-color="neutral500" unset-min-width size="xs" button-class="px-2" @click="showTreatmentPlanDialog = true")

    template(v-else)
      div.pb-4
        template(v-for="(treatment, idx) in treatmentList")
          v-divider(v-if="idx")
          treatment-list-item(ref="treatmentItem" :treatment="treatment" :item-index="idx" :key="`tr-${treatment.id}`" @updated="updateTreatments")

  .dialogs
    treatment-plan-dialog(v-if="showTreatmentPlanDialog" :open.sync="showTreatmentPlanDialog" :edit-mode="editMode" :bulk-mode="bulkMode" :past-mode="pastMode" @closed="closed" @updated="updateDialog")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SearchTreatmentDropdown from '@/components/clinic/consultation/SearchTreatmentDropdown.vue'
import TreatmentListForm from '@/components/clinic/consultation/TreatmentListForm.vue'
import TreatmentListItem from '@/components/clinic/consultation/TreatmentListItem.vue'
import TreatmentPlanDialog from '@/components/clinic/consultation/TreatmentPlanDialog.vue'

export default {
  components: { SearchTreatmentDropdown, TreatmentListForm, TreatmentListItem, TreatmentPlanDialog },
  data() {
    return {
      isFetchingTreatments: false,
      isFetchingActiveMedication: false,
      isInjecting: false,
      showTreatmentListForm: false,
      showTreatmentPlanDialog: false,
      editMode: false,
      bulkMode: false,
      pastMode: false,
      selectedTreatment: null,
      tabs: 0,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      patient: 'treatment/getPatient',
      treatments: 'treatment/getTreatments',
      treatmentForms: 'treatment/getTreatmentForms',
      registration: 'treatment/getRegistration',
    }),
    treatmentList () {
      if (this.tabs === 1) return this.itemList
      if (this.tabs === 2) return this.serviceList
      if (this.tabs === 3) return this.packageList
      if (this.tabs === 4) return this.documentList
      else return this.treatments
    },
    treatmentAddForms () {
      return this.treatmentForms
    },
    itemList () {
      return this.treatments.filter(f => ['medication', 'equipment', 'consumable'].includes(f.product.type))
    },
    serviceList () {
      return this.treatments.filter(f => ['service', 'procedure', 'investigation', 'consultation'].includes(f.product.type))
    },
    packageList () {
      return this.treatments.filter(f => ['bundle'].includes(f.product.type))
    },
    documentList () {
      return this.treatments.filter(f => ['documents'].includes(f.product.type))
    },
  },
  mounted () {
    this.checkInjectConsultation()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchTreatments () {
      this.isFetchingTreatments = true
      let params = {
        fields: 'treatments'
      }
      this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}`, { params })
        .then(resp => {
          this.$store.commit('treatment/setTreatments', resp.data.treatments)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingTreatments = false
        })
    },
    fetchActiveMedication () {
      this.isFetchingActiveMedication = true
      const params = {
        page_size: -1,
        patient: this.patient.id,
        expand: 'medication'
      }
      this.$axios.get(`/clinics/${this.company.slug}/active_medications`, { params })
        .then(resp => {
          let activeMedications = []
          if (resp.data && resp.data.results) {
            activeMedications = resp.data.results.map(m => { return { id: m.id, m_id: m.medication.id, name: m.medication.name } })
          }
          this.$store.commit('treatment/setActiveMedications', activeMedications)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingActiveMedication = false
        })
    },
    handleAddItem (item) {
      let newItem = { ...item, listid: Date.now() }
      let forms = this.treatmentAddForms
      forms.push(newItem)
      this.$store.commit('treatment/updateTreatmentForms', forms)
    },
    updateTreatments () {
      this.fetchTreatments()
      this.fetchActiveMedication()
      this.closed()
    },
    updateForm (index) {
      this.updateTreatments()
      this.removeForm(index)
    },
    updateDialog () {
      this.updateTreatments()
      this.$store.commit('treatment/resetTreatmentForms')
      this.$store.commit('treatment/resetTreatmentEditingStates')
    },
    openTreatmentPlanDialog () {
      this.editMode = true
      this.showTreatmentPlanDialog = true
    },
    openAddBulkTreatmentDialog () {
      this.bulkMode = true
      this.showTreatmentPlanDialog = true
    },
    openAddPastTreatmentDialog () {
      this.pastMode = true
      this.showTreatmentPlanDialog = true
    },
    checkInjectConsultation () {
      // Only inject if status not completed
      if (this.registration?.status !== 'completed' && this.company.genes.find(gene => gene.feature === 'auto_inject_consultation')) {
        this.autoInjectConsultationFee()
      }
    },
    autoInjectConsultationFee () {
      this.isInjecting = true
      const TYPE = 'consultation'
      const hasTreatmentConsultations = this.treatments.some(treatment => treatment?.product?.type === TYPE)
      if (hasTreatmentConsultations) {
        this.isInjecting = false
        return
      }
      let params = {
        page_size: 35,
        search: TYPE,
        status: 'published'
      }
      this.$axios.get(`/clinics/${this.company.slug}/treatment_products`, { params })
        .then(async resp => {
          if (resp.data.count === 0) {
            this.isInjecting = false
            return
          }
          const consultations = resp.data.results.filter(result => result.type === TYPE)
          if (consultations.length > 0) {
            let payload = {
              product: consultations[0].id,
              quantity: 1,
              price: 0
            }
            let params2 = { 
              company: this.company.id,
              product: consultations[0].id
            }
            if (this.registration?.default_payment && this.registration.default_payment.type !== 'self-pay') {
              params2.payment_methods = this.registration.default_payment.panel.id
            } else {
              params2.payment_channel = 'self-pay'
            }
            await this.$axios.get('/product_pricetiers', { params: params2 })
              .then(resp2 => {
                if (resp2.data.count) {
                  payload.price = resp2.data.results[0].price || 0
                  payload.extra_data = {
                    price_tier: resp2.data.results[0].id,
                    price_tier_name: resp2.data.results[0].name,
                  }
                }
              })
              .catch(() => {})
            const endpoint = `/clinics/${this.company.slug}/invoices/${this.leadPid}`
            await this.$axios.patch(endpoint, { products: [payload] })
              .then(resp => {
                this.updateActivityLog(endpoint, payload, resp.data, 'POST', resp.data.id, 'consultation', `Auto inject consultation (${this.leadPid})`)
                this.updateTreatments()
              })
              .catch((err) => {
                let message = this.$t('error_message.unable_to_complete_request')
                if (err.response?.status === 400 && err.response?.data?.code === '444101') {
                  message = 'Item quantity more than available stock. Please adjust the quantity'
                }       
                if (err.response.data) {
                  if (err.response.data.detail) {
                    message = err.response.data.detail
                  } else {
                    let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                    if (data) { message = data }
                  }
                }
                this.showMessage(['error', message])
              })
              .finally(() => {
                this.isInjecting = false
              })
          } else {
            this.isInjecting = false
          }
        })
        .catch(() => {
          this.isInjecting = false
        })
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: objectRef,
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    closed () {
      this.selectedTreatment = null
      this.showTreatmentPlanDialog = false
      this.bulkMode = false
      this.pastMode = false
      this.editMode = false
    },
    removeForm (index) {
      let forms = this.treatmentAddForms
      forms.splice(index, 1)
      this.$store.commit('treatment/updateTreatmentForms', forms)
    },
    changeTab () {
      this.$store.commit('treatment/resetTreatmentEditingStates')
      this.$refs.treatmentItem.forEach((item) => {
        item.updateReadState()
      })
    },
  },
};
</script>

<style lang="scss" scoped>
.treatment-filters {
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}
.selected-button {
  background: #EEEEEE !important;
  border-radius: 16px;
}
.custom-chip {
  margin-left: 8px;
  padding-right: 5px;
  padding-left: 5px;
  border-radius: 5px;
}
</style>